import { ProgramContainer } from "../entities/formTemplateDataType";
import { DashboardIdApplicationsPageRedirectHandler } from "./DashboardIdApplicationsPageRedirectHandler";
import { EhocmPartnerIdPageRedirectHandler } from "./EhocmPartnerIdPageRedirectHandler";
import { GlpPageRedirectHandler } from "./GlpPageRedirectHandler";
import { PlpPageRedirectHandler } from "./PlpPageRedirectHandler";
import { convertToTitleCase } from "../utilities/Utils";

const Handlers = {  // To define the list of handlers that extend AbstractPageRedirectHandler.
   DashboardIdAppicationsPageRedirectHandler: DashboardIdApplicationsPageRedirectHandler,
   EhocmPartnerIdPageRedirectHandler: EhocmPartnerIdPageRedirectHandler,
   GlpPageRedirectHandler: GlpPageRedirectHandler,
   PlpPageRedirectHandler: PlpPageRedirectHandler
}

/**
 * Lookup <category-id+'PageRedirectHandler'> handler, and execute its isRedirectionRequired method.
 * @param categoryId Category id of the application.
 * @returns If redirection is needed.
 */
export const isRedirectionRequired = (categoryId: string): boolean => {
  const handler = getHandler(categoryId);
  if (handler) {
    return handler.isRedirectionRequired();
  }
  return false;
}

/**
 * Lookup <category-id+'PageRedirectHandler'> handler, and execute its isProcessSuccessful method.
 * @param categoryId Category id of the application.
 * @returns If process is successful.
 */
export const isProcessSuccessful = (categoryId: string): boolean => {
  const handler = getHandler(categoryId);
  if (handler) {
    return handler.isProcessSuccessful();
  }
  return false;
}

/**
 * Lookup the transaction-id returned from Payment Gateway.
 * @param categoryId Category id of the application.
 * @returns The transaction-id.
 */
export const getTransactionId = (categoryId: string): string => {
  const handler = getHandler(categoryId);
  if (handler) {
    return handler.getTransactionId();
  }
  return null;
}

/**
 * Lookup <category-id+'PageRedirectHandler'> handler, and execute its getPageForSuccessfulProcess method.
 * @param categoryId Category id of the application.
 * @returns The page to redirect to when the process is successful.
 */
export const getPageForSuccessfulProcess = (categoryId: string): number => {
  const handler = getHandler(categoryId);
  if (handler) {
    return handler.getPageForSuccessfulProcess();
  }
  return 0;
}

/**
 * Lookup <category-id+'PageRedirectHandler'> handler, and execute its getPageForUnsuccessfulProcess method.
 * @param categoryId Category id of the application.
 * @returns The page to redirect to when the process is unsuccessful.
 */
export const getPageForUnsuccessfulProcess = (categoryId: string): number => {
  const handler = getHandler(categoryId);
  if (handler) {
    return handler.getPageForUnsuccessfulProcess();
  }
  return 0;
}

/**
 * Lookup <category-id+'PageRedirectHandler'> handler, and execute its getErrorMessageForUnsuccessfulProcess method.
 * @param categoryId Category id of the application.
 * @param isEng Language of error message.
 * @returns The error message to display when the process is unsuccessful.
 */
export const getErrorMessageForUnsuccessfulProcess = (categoryId: string, language: string): number => {
  const handler = getHandler(categoryId);
  if (handler) {
    return handler.getErrorMessageForUnsuccessfulProcess(language);
  }
  return 0;
}

/**
 * Lookup <category-id+'PageRedirectHandler'> handler, and execute its populateApiRequest method.
 * @param categoryId Category id of the application.
 * @param programContainer The application data container.
 * @param parameters Optional parameters.
 */
export const populateApiRequest = (categoryId: string, programContainer: ProgramContainer, parameters?: any[]): void => {
  const handler = getHandler(categoryId);
  if (handler) {
    return handler.populateApiRequest(programContainer, parameters);
  }
  return;
}

export const getHandler = (categoryId: string): any => {
  const handlerNameSuffix = 'PageRedirectHandler';
  const handlerName = convertToTitleCase(categoryId).concat(handlerNameSuffix);
  const handler = Handlers[handlerName] ? Object.create(Handlers[handlerName].prototype) : null;
  if (handler) {
    handler.constructor.apply(handler);
    return handler;
  } else {
    console.log('handler ' + handlerName + ' not found');
    return null;
  }
}
